import {ChangeDetectionStrategy, Component, HostListener, inject, Input, OnInit, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Subject} from 'rxjs';
import {CdkTrapFocus} from '@angular/cdk/a11y';

import {ModalComponent} from '../../../services/modal/modal-service';
import {FormSnapshot} from '../../dynamic-form';
import {SafeHtmlPipe} from '../../../pipes/safe-html/safe-html.pipe';
import {BrandingService} from '../../../services/branding/branding-service';

@Component({
  selector: 'app-contract-preview-modal',
  standalone: true,
  imports: [CdkTrapFocus, SafeHtmlPipe],
  templateUrl: './contract-preview-modal.html',
  styleUrl: './contract-preview-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractPreviewModal implements ModalComponent, OnInit {
  @Input({required: true}) url!: string;
  @Input() snapshot: FormSnapshot = {};

  onConfirm = new Subject<void>();
  onCancel = new Subject<void>();

  readonly isLoading = signal(true);
  readonly hasError = signal(false);
  readonly htmlContent = signal('');

  private readonly http = inject(HttpClient);
  private readonly brandingService = inject(BrandingService);

  ngOnInit(): void {
    this.http.get(this.url, {responseType: 'text'}).subscribe({
      next: (html) => {
        this.htmlContent.set(this.replacePlaceholders(html));
        this.isLoading.set(false);
      },
      error: () => {
        this.hasError.set(true);
        this.isLoading.set(false);
      },
    });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.accept();
    }
  }

  accept(): void {
    this.onConfirm.next();
  }

  download(): void {
    const html = this.htmlContent();
    if (!html) return;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Documento</title></head><body>${html}</body></html>`);
    doc.close();

    iframe.contentWindow!.onafterprint = () => document.body.removeChild(iframe);
    iframe.contentWindow!.focus();
    iframe.contentWindow!.print();
  }

  // contract-preview-modal.ts

  private replacePlaceholders(html: string): string {
    const s = this.snapshot;
    const tipologia = (s['tipologiaSoggetto']?.['tipologia'] as string) ?? '';
    const isFisica  = tipologia === 'Persona Fisica';

    const anagrafica = s['anagraficaStudio']       ?? {};
    const sede       = s['sede']                   ?? {};
    const rl         = s['rappresentanteLegale']   ?? {};

    const today = new Date();
    const data  = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;

    // ── Placeholder testuali (invariati) ──────────────────────────────────────

    const ragioneSociale = (anagrafica['ragioneSociale'] as string) ?? '';
    const nome = !isFisica ? ((rl['nome'] as string) ?? '') : ((anagrafica['nome'] as string) ?? '');
    const cognome = !isFisica ? ((rl['cognome'] as string) ?? '') : ((anagrafica['cognome'] as string) ?? '');
    const codiceFiscale = !isFisica
      ? ((rl['codiceFiscale'] as string) ?? '')
      : ((anagrafica['codiceFiscalePF'] as string) ?? '');
    const partitaIva = isFisica
      ? ((anagrafica['partitaIvaPF'] as string) ?? '')
      : ((anagrafica['partitaIvaPG'] as string) ?? '');

    const citta     = (sede['citta']     as string) ?? '';
    const provincia = (sede['provincia'] as string) ?? '';
    const indirizzo = (sede['indirizzo'] as string) ?? '';
    const luogoStudio = citta;

    // L'iscrizione è mappata nella sezione rappresentanteLegale per entrambe le tipologie
    const iscrizioneSource = rl;
    const luogoOrdine  = (iscrizioneSource['sedeProvincia'] as string) ?? '';
    const numeroOrdine = (iscrizioneSource['nIscrizione']   as string) ?? '';
    const sezione      = (iscrizioneSource['ordine']        as string) ?? '';

    const map: Record<string, string> = {
      '%ragione_sociale%': ragioneSociale,
      '%nome%':            nome,
      '%cognome%':         cognome,
      '%codice_fiscale%':  codiceFiscale,
      '%partita_iva%':     partitaIva,
      '%indirizzo%':       indirizzo,
      '%luogo_studio%':    luogoStudio,
      '%luogo_ordine%':    luogoOrdine,
      '%numero_ordine%':   numeroOrdine,
      '%sezione%':         sezione,
      '%data%':            data,
      // Derivato dall'hostname, non fisso: il documento che l'utente accetta a
      // schermo deve riportare lo stesso marchio del PDF che il backend genera
      // e allega alla PEC di attivazione, altrimenti i due divergono.
      '%nome_software%':   this.brandingService.getNomeSoftware(),
    };

    html = html.replace(/%[^%]+%/g, (match) => map[match] ?? match);

    // ── Visibilità sezioni (logica ASP portata in TS) ─────────────────────────

    // isnumeric(num_iscrizione): true se la stringa è composta solo da cifre
    const hasIscrizione = /^\d+$/.test(numeroOrdine.trim());

    if (isFisica) {
      // Nasconde le sezioni PG
      html = this.hideById(html, 'persona_giuridica_con_ODCEC');
      html = this.hideById(html, 'persona_giuridica_senza_ODCEC');
      // Mostra la sezione PF corretta in base all'iscrizione
      html = hasIscrizione
        ? this.hideById(html, 'persona_fisica_senza_ODCEC')
        : this.hideById(html, 'persona_fisica_con_ODCEC');
      // Firma: Cognome Nome
      const firma = [cognome, nome].filter(Boolean).join(' ');
      html = html.replace(`<p id='firma'></p>`, `<p id='firma'>${firma}</p>`);
    } else {
      // Nasconde le sezioni PF
      html = this.hideById(html, 'persona_fisica_con_ODCEC');
      html = this.hideById(html, 'persona_fisica_senza_ODCEC');
      // Mostra la sezione PG corretta in base all'iscrizione
      html = hasIscrizione
        ? this.hideById(html, 'persona_giuridica_senza_ODCEC')
        : this.hideById(html, 'persona_giuridica_con_ODCEC');
      // Firma: Ragione Sociale
      html = html.replace(`<p id='firma'></p>`, `<p id='firma'>${ragioneSociale}</p>`);
    }

    // Nasconde i riferimenti al commercialista se manca la provincia ODCEC
    if (!luogoOrdine) {
      html = this.hideById(html, 'span_commercialista1');
      html = this.hideById(html, 'span_commercialista2');
    }

    return html;
  }

  /**
   * Aggiunge style="display:none" all'elemento con l'id specificato.
   * Gestisce sia virgolette singole che doppie nell'HTML sorgente.
   */
  private hideById(html: string, id: string): string {
    return html.replace(
      new RegExp(`id=["']${id}["']`, 'g'),
      `id="${id}" style="display:none"`,
    );
  }
}
